import React, { useState, useEffect } from 'react';
import { postDataAPI, getDataAPI, deleteDataAPI } from '../../../utils/fetchData';
import Modal from '../../../components/Modal';
import { useModal } from '../../../hooks/useModal';

const CrewTab = ({ show, onRefresh }) => {
  const { modalConfig, showModal, closeModal } = useModal();
  const [crew, setCrew] = useState(show.crew || []);
  const [showAddModal, setShowAddModal] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [mode, setMode] = useState('select'); // 'select' or 'create'
  const [selectedRole, setSelectedRole] = useState('all');

  // Form state for creating new crew member
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    category: 'other',
    contactInfo: {
      phone: '',
      email: ''
    },
    notes: '',
    saveToContacts: true
  });

  const crewRoles = [
    'Tour Manager',
    'Production Manager',
    'FOH Engineer',
    'Monitor Engineer',
    'Stage Manager',
    'Backline Tech',
    'Lighting Designer',
    'Video Engineer',
    'Security',
    'Merch Manager',
    'Driver'
  ];

  const crewCategories = [
    { value: 'tour-management', label: 'Tour Management' },
    { value: 'audio', label: 'Audio' },
    { value: 'production', label: 'Production' },
    { value: 'lighting', label: 'Lighting' },
    { value: 'video', label: 'Video' },
    { value: 'backline', label: 'Backline' },
    { value: 'security', label: 'Security' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    if (showAddModal && mode === 'select') {
      loadContacts();
    }
  }, [showAddModal, mode, selectedRole]);

  const loadContacts = async () => {
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const params = new URLSearchParams();
      
      if (selectedRole !== 'all') {
        params.append('role', selectedRole);
      }
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      
      const response = await getDataAPI(`user/contacts?${params.toString()}`, token);
      if (response.data.status === 200) {
        // Filter contacts that have crew-related roles
        const crewContacts = (response.data.data || []).filter(contact => 
          contact.roles?.some(role => 
            crewRoles.includes(role) || 
            role.includes('Engineer') || 
            role.includes('Manager') ||
            role.includes('Tech') ||
            role.includes('Designer')
          )
        );
        setContacts(crewContacts);
      }
    } catch (error) {
      console.error('Error loading contacts:', error);
    }
  };

  const handleAddFromContact = async (contact) => {
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      
      // Add crew member to show
      const crewData = {
        tourShowId: show._id,
        name: contact.name,
        role: contact.primaryRole,
        category: determineCategoryFromRole(contact.primaryRole),
        contactInfo: {
          phone: contact.contactInfo?.phone || '',
          email: contact.contactInfo?.email || ''
        },
        showSpecific: {
          notes: contact.notes || ''
        }
      };

      const response = await postDataAPI('user/tm/crew', crewData, token);
      
      if (response.data.status === 201) {
        // Update contact's last used timestamp
        await postDataAPI(`user/contacts/${contact._id}/update-last-used`, {}, token);
        
        setShowAddModal(false);
        onRefresh();
        showModal('Success', 'Crew member added', 'success');
      }
    } catch (error) {
      console.error('Error adding crew member:', error);
      showModal('Error', 'Failed to add crew member', 'error');
    }
  };

  const determineCategoryFromRole = (role) => {
    const roleMap = {
      'FOH Engineer': 'audio',
      'Monitor Engineer': 'audio',
      'Tour Manager': 'tour-management',
      'Production Manager': 'production',
      'Lighting Designer': 'lighting',
      'Video Engineer': 'video',
      'Backline Tech': 'backline',
      'Security': 'security',
      'Stage Manager': 'production'
    };
    return roleMap[role] || 'other';
  };

  const handleCreateCrew = async () => {
    try {
      if (!formData.name || !formData.role) {
        showModal('Error', 'Name and role are required', 'error');
        return;
      }

      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      
      // If saving to contacts, create contact first
      if (formData.saveToContacts) {
        const contactData = {
          name: formData.name,
          primaryRole: formData.role,
          roles: [formData.role],
          contactInfo: formData.contactInfo,
          notes: formData.notes
        };
        await postDataAPI('user/contacts', contactData, token);
      }

      // Add crew member to show
      const crewData = {
        tourShowId: show._id,
        name: formData.name,
        role: formData.role,
        category: formData.category,
        contactInfo: formData.contactInfo,
        showSpecific: {
          notes: formData.notes
        }
      };

      const response = await postDataAPI('user/tm/crew', crewData, token);
      
      if (response.data.status === 201) {
        setShowAddModal(false);
        setFormData({
          name: '',
          role: '',
          category: 'other',
          contactInfo: { phone: '', email: '' },
          notes: '',
          saveToContacts: true
        });
        onRefresh();
        showModal('Success', 'Crew member added successfully', 'success');
      }
    } catch (error) {
      console.error('Error creating crew member:', error);
      showModal('Error', 'Failed to add crew member', 'error');
    }
  };

  const handleDeleteCrew = (member) => {
    showModal(
      'Remove Crew Member',
      `Remove ${member.name} from crew?`,
      'warning',
      async () => {
        try {
          const token = JSON.parse(localStorage.getItem('dizeeToken'));
          await deleteDataAPI(`user/tm/crew/${member._id}`, {}, token);
          onRefresh();
          closeModal();
        } catch (error) {
          console.error('Error deleting crew member:', error);
          showModal('Error', 'Failed to remove crew member', 'error');
        }
      },
      true
    );
  };

  const getCategoryColor = (category) => {
    const colors = {
      'tour-management': 'bg-blue-500/20 text-blue-400',
      'audio': 'bg-green-500/20 text-green-400',
      'production': 'bg-purple-500/20 text-purple-400',
      'lighting': 'bg-yellow-500/20 text-yellow-400',
      'video': 'bg-pink-500/20 text-pink-400',
      'backline': 'bg-cyan-500/20 text-cyan-400',
      'security': 'bg-red-500/20 text-red-400',
      'other': 'bg-gray-500/20 text-gray-400'
    };
    return colors[category] || colors.other;
  };

  return (
    <div className="px-4 pb-6">
      {crew.length === 0 ? (
        <div className="text-left py-[60px]">
          <h3 className="text-white text-[12px] font-semibold mb-2">
            No crew members found
          </h3>
          <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] mb-6 max-w-[300px]">
            Add crew members to manage your tour team for this show.
          </p>
          <button 
            onClick={() => {
              setMode('select');
              setShowAddModal(true);
            }}
            className="bg-[#FF9500] hover:bg-[#FF8800] text-black px-5 py-2 rounded-lg text-[12px] font-medium transition-colors"
          >
            Add Crew Member
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {crew.map((member, index) => (
            <div
              key={member._id || index}
              className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[rgba(255,255,255,0.3)] transition-all"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
              <p className="text-white text-[12px] font-semibold mb-1">
                {member.name}
              </p>
              <p className="text-[rgba(255,255,255,0.6)] text-[10px] mb-2">
                {member.role}
              </p>
                  {member.category && (
                    <span className={`inline-block px-2 py-1 rounded text-[9px] font-medium ${getCategoryColor(member.category)}`}>
                      {member.category.replace('-', ' ').toUpperCase()}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => handleDeleteCrew(member)}
                  className="text-white text-[12px] font-normal hover:opacity-70"
                >
                  Remove
                </button>
              </div>
              <div className="space-y-1">
                {member.contactInfo?.phone && (
                  <p className="text-[rgba(255,255,255,0.5)] text-[10px]">
                    Phone: {member.contactInfo.phone}
                  </p>
                )}
                {member.contactInfo?.email && (
                  <p className="text-[rgba(255,255,255,0.5)] text-[10px]">
                    Email: {member.contactInfo.email}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Crew Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowAddModal(false)}>
          <div 
            className="w-full max-w-[400px] max-h-[70vh] mx-4 transform overflow-hidden rounded-lg bg-black border border-[rgba(255,255,255,0.1)] text-left align-middle shadow-xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-[rgba(255,255,255,0.1)] flex-shrink-0">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-white text-[14px] font-medium">Add Crew Member</h2>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="text-white text-[12px] font-normal hover:opacity-70"
                >
                  Close
                </button>
              </div>

              {/* Mode Toggle */}
              <div className="flex gap-2">
                <button
                  onClick={() => setMode('select')}
                  className={`flex-1 py-2 rounded-[8px] text-[12px] font-medium transition-colors border ${
                    mode === 'select'
                      ? 'bg-[#FF9500] text-black border-[#FF9500]'
                      : 'bg-[rgba(255,255,255,0.05)] text-white border-[rgba(255,255,255,0.1)] hover:bg-[rgba(255,149,0,0.1)] hover:border-[rgba(255,149,0,0.3)]'
                  }`}
                >
                  From Contacts
                </button>
                <button
                  onClick={() => setMode('create')}
                  className={`flex-1 py-2 rounded-[8px] text-[12px] font-medium transition-colors border ${
                    mode === 'create'
                      ? 'bg-[#FF9500] text-black border-[#FF9500]'
                      : 'bg-[rgba(255,255,255,0.05)] text-white border-[rgba(255,255,255,0.1)] hover:bg-[rgba(255,149,0,0.1)] hover:border-[rgba(255,149,0,0.3)]'
                  }`}
                >
                  Create New
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-6 space-y-4">
              {mode === 'select' ? (
                <>
                  {/* Role Filter */}
                  <div className="mb-3">
                    <select
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-[8px] px-3 py-2 text-white text-[12px] focus:outline-none focus:border-[#FF9500]"
                      style={{ backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%23ffffff\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpolyline points=\'6,9 12,15 18,9\'%3E%3C/polyline%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center', backgroundSize: '16px', appearance: 'none' }}
                    >
                      <option value="all">All Roles</option>
                      {crewRoles.map(role => (
                        <option key={role} value={role}>{role}</option>
                      ))}
                    </select>
                  </div>

                  {/* Search */}
                  <div className="mb-4">
                    <input
                      type="text"
                      placeholder="Search contacts..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        loadContacts();
                      }}
                      className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-[8px] px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] focus:outline-none focus:border-[#FF9500]"
                    />
                  </div>

                  {/* Contacts List */}
                  <div className="space-y-2">
                    {contacts.length === 0 ? (
                      <div className="text-left py-8">
                        <p className="text-[rgba(255,255,255,0.7)] text-[12px] mb-2">
                          No crew contacts found
                        </p>
                        <p className="text-[rgba(255,255,255,0.5)] text-[11px] mb-4">
                          Create contacts in the Contacts app first
                        </p>
                        <button
                          onClick={() => setMode('create')}
                          className="text-[#FF9500] text-[12px] hover:opacity-70 font-medium"
                        >
                          Or create a new crew member →
                        </button>
                      </div>
                    ) : (
                      contacts.map(contact => (
                        <button
                          key={contact._id}
                          onClick={() => handleAddFromContact(contact)}
                          className="w-full text-left bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-[8px] p-3 hover:bg-[rgba(255,149,0,0.1)] hover:border-[rgba(255,149,0,0.3)] transition-colors"
                        >
                          <p className="text-white text-[12px] font-medium mb-1">
                            {contact.name}
                          </p>
                          <p className="text-[rgba(255,255,255,0.7)] text-[11px] mb-1">
                            {contact.primaryRole}
                          </p>
                          {contact.contactInfo?.email && (
                            <p className="text-[rgba(255,255,255,0.5)] text-[10px]">
                              {contact.contactInfo.email}
                            </p>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* Create New Crew Member Form */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-white text-[12px] font-medium mb-2">
                        Name *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-[8px] px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] focus:outline-none focus:border-[#FF9500] transition-colors"
                        placeholder="Crew member name"
                      />
                    </div>

                    <div>
                      <label className="block text-white text-[12px] font-medium mb-2">
                        Role *
                      </label>
                      <select
                        value={formData.role}
                        onChange={(e) => {
                          const role = e.target.value;
                          setFormData(prev => ({
                            ...prev,
                            role,
                            category: determineCategoryFromRole(role)
                          }));
                        }}
                        className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-[8px] px-3 py-2 text-white text-[12px] focus:outline-none focus:border-[#FF9500] transition-colors cursor-pointer"
                        style={{ backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%23ffffff\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpolyline points=\'6,9 12,15 18,9\'%3E%3C/polyline%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center', backgroundSize: '16px', appearance: 'none' }}
                      >
                        <option value="">Select a role</option>
                        {crewRoles.map(role => (
                          <option key={role} value={role}>{role}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-white text-[12px] font-medium mb-2">
                        Category
                      </label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                        className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-[8px] px-3 py-2 text-white text-[12px] focus:outline-none focus:border-[#FF9500] transition-colors cursor-pointer"
                        style={{ backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%23ffffff\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpolyline points=\'6,9 12,15 18,9\'%3E%3C/polyline%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center', backgroundSize: '16px', appearance: 'none' }}
                      >
                        {crewCategories.map(cat => (
                          <option key={cat.value} value={cat.value}>{cat.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-white text-[12px] font-medium mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        value={formData.contactInfo.email}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          contactInfo: { ...prev.contactInfo, email: e.target.value }
                        }))}
                        className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-[8px] px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] focus:outline-none focus:border-[#FF9500] transition-colors"
                        placeholder="email@example.com"
                      />
                    </div>

                    <div>
                      <label className="block text-white text-[12px] font-medium mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        value={formData.contactInfo.phone}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          contactInfo: { ...prev.contactInfo, phone: e.target.value }
                        }))}
                        className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-[8px] px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] focus:outline-none focus:border-[#FF9500] transition-colors"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>

                    <div>
                      <label className="block text-white text-[12px] font-medium mb-2">
                        Notes
                      </label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                        rows={3}
                        className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-[8px] px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] focus:outline-none focus:border-[#FF9500] transition-colors resize-none"
                        placeholder="Additional notes..."
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="saveToContacts"
                        checked={formData.saveToContacts}
                        onChange={(e) => setFormData(prev => ({ ...prev, saveToContacts: e.target.checked }))}
                        className="w-4 h-4 text-[#FF9500] rounded focus:ring-[#FF9500]"
                      />
                      <label htmlFor="saveToContacts" className="text-[rgba(255,255,255,0.7)] text-[12px]">
                        Save to Contacts for future use
                      </label>
                    </div>

                    <button
                      onClick={handleCreateCrew}
                      className="w-full bg-[#FF9500] hover:bg-[#FF8800] text-black text-[12px] font-medium py-2 px-4 rounded-[8px] transition-all focus:outline-none"
                    >
                      Add Crew Member
                    </button>
                  </div>
                </>
              )}
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={modalConfig.isOpen}
        onClose={closeModal}
        onConfirm={modalConfig.onConfirm}
        title={modalConfig.title}
        message={modalConfig.message}
        type={modalConfig.type}
        showCancel={modalConfig.showCancel}
        confirmText={modalConfig.confirmText}
        cancelText={modalConfig.cancelText}
      />
    </div>
  );
};

export default CrewTab;
